import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Save, Pencil, Receipt } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, moneyK, fecha, mesLabel, Title, Badge, Empty, ESTADO_RENOVACION } from './ui';

/* El punto de color es el estado del contrato (activo / pausado / baja) y la
   columna nueva es en qué punto está la renovación de ESTE mes. Son dos cosas
   distintas y antes solo se veía la segunda. */
const ESTADO_CONTRATO = { activo: { label: 'Activo', color: '#22c55e' }, pausado: { label: 'En pausa', color: '#f59e0b' }, baja: { label: 'De baja', color: '#64748b' } };
const ESTADO_MES = {
  al_dia:        { label: 'Al día',        color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
  emitir_cobro:  { label: 'Emitir cobro',  color: '#f59e0b', bg: 'rgba(245,158,11,0.16)' },
  falta_avisar:  { label: 'Falta avisar',  color: '#f97316', bg: 'rgba(249,115,22,0.16)' },
  cobro_emitido: { label: 'Cobro emitido', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  pagado:        { label: 'Pagado',        color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  esperando_pago:{ label: 'Esperando pago', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  en_gracia:     { label: 'Venció sin renovar', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  no_renovo:     { label: 'No renovó',     color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
  pausado:       { label: 'En pausa',      color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  baja:          { label: 'Baja',          color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
};

const PASOS = ['por_preparar', 'preparada', 'enviada', 'renovada', 'no_renovo', 'en_gracia'];

export default function AgenciaRenovaciones() {
  const navigate = useNavigate();
  const [mes, setMes] = useState(new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 7));
  const [data, setData] = useState({ renovaciones: [], por_mes: [] });
  const [edit, setEdit] = useState(null);
  const [vence, setVence] = useState(null);   // id de la renovación con la fecha abierta

  const load = useCallback(() => { api.get('/agency/renovaciones', { params: { mes } }).then(r => setData(r.data)).catch(() => {}); }, [mes]);
  useEffect(() => { load(); }, [load]);

  const actualizar = async (c, cambios) => {
    try { await api.put('/agency/contratos/' + c.id, cambios); toast.success('Actualizado'); setEdit(null); load(); }
    catch { toast.error('Error'); }
  };

  /* La fecha de renovación se corre seguido: el cliente pagó tarde, se renovó
     con unos días de diferencia. Se edita en la celda, sin abrir nada. */
  /* Generar el cobro de la renovación. Hasta ahora esto solo se podía hacer
     desde el servidor: la pantalla mostraba "Emitir cobro" y no había con qué.
     Una vez generado, la factura se carga sobre ese cobro desde la ficha del
     cliente, y el cliente lo ve en su portal. */
  const [generando, setGenerando] = useState(null);
  const generarCobro = async (r) => {
    if (!window.confirm(`¿Generar el cobro de la renovación de ${r.cliente} por ${money(r.monto)}?\n\nVence el ${fecha(r.proximo_vencimiento)}. Todavía no se le avisa nada: eso lo decidís después.`)) return;
    setGenerando(r.id);
    try {
      const { data } = await api.post(`/agency/contratos/${r.id}/generar-cobro`, {});
      toast.success(data.duplicado ? 'Ese cobro ya estaba generado' : 'Cobro generado · cargale la factura desde la ficha del cliente');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo generar'); }
    finally { setGenerando(null); }
  };

  const guardarVence = async (r, valor) => {
    setVence(null);
    if (!valor || valor === String(r.proximo_vencimiento || '').slice(0, 10)) return;
    try {
      await api.put('/agency/contratos/' + r.id, { proximo_vencimiento: valor });
      toast.success('Fecha de renovación actualizada');
      load();
    } catch { toast.error('No se pudo guardar'); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  // Un hosting en pausa se ve en la lista pero no suma: no se le va a cobrar
  const activas = data.renovaciones.filter(r => r.estado === 'activo');
  const total = activas.reduce((a, r) => a + Number(r.monto || 0), 0);
  const pend = activas.filter(r => ['por_preparar', 'preparada', 'enviada', 'en_gracia'].includes(r.estado_renovacion));

  return (
    <div>
      <Title right={
        <select value={mes} onChange={e => setMes(e.target.value)} style={{ width: 'auto', fontSize: 13 }}>
          {data.por_mes.map(m => <option key={m.mes} value={m.mes}>{mesLabel(m.mes)} · {m.cant} renovaciones</option>)}
          {!data.por_mes.some(m => m.mes === mes) && <option value={mes}>{mesLabel(mes)}</option>}
        </select>
      }>Renovaciones de hosting</Title>

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px', display: 'flex', gap: 22, flexWrap: 'wrap', fontSize: 13 }}>
        <span><b>{activas.length}</b> renovaciones este mes</span>
        <span>Total: <b>{moneyK(total)}</b></span>
        <span style={{ color: pend.length ? '#f59e0b' : 'var(--text-muted)' }}><b>{pend.length}</b> sin cerrar</span>
        <span style={{ color: 'var(--text-muted)' }}>Vencen el último día del mes. Nada se envía al cliente sin que lo mandes vos.</span>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Cliente</th><th style={th}>Web</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado del mes</th><th style={th}>Renovación</th><th style={th}></th></tr></thead>
            <tbody>
              {data.renovaciones.length === 0 ? <tr><td colSpan={7} style={td}><Empty>Este mes no vence ningún hosting</Empty></td></tr>
                : data.renovaciones.map(r => {
                  const e = ESTADO_RENOVACION[r.estado_renovacion] || {};
                  const ec = ESTADO_CONTRATO[r.estado] || ESTADO_CONTRATO.activo;
                  const em = ESTADO_MES[r.estado_mes] || ESTADO_MES.al_dia;
                  return (
                    <tr key={r.id}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                          onClick={() => navigate('/agencia/clientes/' + r.cliente_id)}>
                          <span title={ec.label} style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: ec.color }} />
                          <span style={{ fontWeight: 600 }}>{r.cliente}</span>
                        </div>
                      </td>
                      <td style={td}>{r.web ? <a href={r.web.startsWith('http') ? r.web : 'https://' + r.web} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Ver sitio <ExternalLink size={11} /></a> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {vence === r.id ? (
                          <input type="date" autoFocus defaultValue={String(r.proximo_vencimiento || '').slice(0, 10)}
                            onBlur={ev => guardarVence(r, ev.target.value)}
                            onKeyDown={ev => { if (ev.key === 'Enter') guardarVence(r, ev.target.value); if (ev.key === 'Escape') setVence(null); }}
                            style={{ width: 150, fontSize: 12, padding: '4px 8px' }} />
                        ) : (
                          <button className="celda-editable" title="Cambiar la fecha de renovación" onClick={() => setVence(r.id)}>
                            {fecha(r.proximo_vencimiento)}<Pencil size={11} />
                          </button>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                        {edit === r.id
                          ? <input type="number" defaultValue={r.monto} id={'m' + r.id} style={{ width: 120, fontSize: 13 }} />
                          : money(r.monto)}
                      </td>
                      <td style={td}><Badge label={em.label} color={em.color} bg={em.bg} /></td>
                      <td style={td}>
                        <select value={r.estado_renovacion || 'por_preparar'} onChange={ev => actualizar(r, { estado_renovacion: ev.target.value })}
                          style={{ width: 'auto', fontSize: 12, padding: '4px 8px', color: e.color, fontWeight: 600 }}>
                          {PASOS.map(p => <option key={p} value={p}>{ESTADO_RENOVACION[p].label}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        {edit === r.id
                          ? <button className="btn btn-primary btn-sm" onClick={() => actualizar(r, { monto: document.getElementById('m' + r.id).value })}><Save size={13} /> Guardar</button>
                          : (
                            <>
                              {!r.cobro_id && r.estado === 'activo' && r.estado_renovacion !== 'no_renovo' && (
                                <button className="btn btn-ghost btn-sm" disabled={generando === r.id}
                                  onClick={() => generarCobro(r)} title="Generar el cobro para poder facturarlo y que lo vea en su portal">
                                  <Receipt size={13} /> Generar cobro
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => setEdit(r.id)}>Editar monto</button>
                            </>
                          )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {data.por_mes.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Renovaciones por mes</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.por_mes.map(m => (
              <button key={m.mes} className="btn btn-sm" onClick={() => setMes(m.mes)}
                style={{ border: '1px solid ' + (m.mes === mes ? 'var(--accent)' : 'var(--border)'), background: m.mes === mes ? 'var(--accent)' : 'transparent', color: m.mes === mes ? '#fff' : 'var(--text-secondary)' }}>
                {mesLabel(m.mes)} · {m.cant} · {moneyK(m.total)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
